import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { newsService } from '../../../services/newsService';
import { NewsItem } from '../../../types';

type AnalysisLocationState = {
  analysis?: NewsItem;
};

const AnalysisDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { analysisId } = useParams<{ analysisId: string }>();
  const location = useLocation();
  const state = (location.state as AnalysisLocationState | null) || null;
  const [analysis, setAnalysis] = useState<NewsItem | null>(state?.analysis || null);
  const [isLoading, setIsLoading] = useState(!state?.analysis);

  const decodedAnalysisId = useMemo(() => decodeURIComponent(analysisId || ''), [analysisId]);

  useEffect(() => {
    const loadAnalysis = async () => {
      if (analysis) return;
      if (!decodedAnalysisId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const allAnalyses = await newsService.getNews();
        const found = allAnalyses.find(
          (item) => (item.analysisId || item.id) === decodedAnalysisId
        );
        setAnalysis(found || null);
      } catch (error) {
        console.error('Erro ao carregar detalhes da análise:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalysis();
  }, [analysis, decodedAnalysisId]);

  const formatDateTime = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen-1/2 gap-4">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-700 rounded-full animate-spin"></div>
        <p>Carregando análise...</p>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Análise não encontrada</h1>
          <p className="text-gray-600 mb-4">Não foi possível localizar os detalhes dessa análise.</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 rounded-lg bg-blue-700 text-white font-semibold hover:bg-blue-800 transition-colors"
          >
            Voltar para o dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Voltar para listagem
        </button>
      </div>

      <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-200 shadow-sm mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-xs md:text-sm text-blue-700 font-semibold">{analysis.company}</span>
          <span className="text-xs md:text-sm text-gray-500">{formatDateTime(analysis.time)}</span>
        </div>
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">{analysis.title}</h1>
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fato</p>
            <p className="text-sm text-gray-700 leading-relaxed">{analysis.fact || 'Fato não informado.'}</p>
          </div>
          <div className="bg-blue-50/70 rounded-lg border border-blue-100 p-3">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">Análise</p>
            <p className="text-sm text-gray-700 leading-relaxed">{analysis.summary}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-5 md:p-7 border border-gray-200 shadow-sm">
        <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">
          Notícias associadas ({analysis.analysisNews?.length || 0})
        </h2>

        {analysis.analysisNews && analysis.analysisNews.length > 0 ? (
          <div className="space-y-3">
            {analysis.analysisNews.map((news, index) => (
              <a
                key={`${news.url}-${index}`}
                href={news.url}
                target="_blank"
                rel="noreferrer"
                className="block rounded-lg border border-gray-200 p-4 hover:border-blue-200 hover:bg-blue-50/40 transition-colors"
              >
                <p className="text-sm md:text-base font-semibold text-gray-900 mb-1">{news.title}</p>
                <p className="text-xs text-gray-500">{formatDateTime(news.time)}</p>
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-600">Nenhuma notícia associada encontrada para esta análise.</p>
        )}
      </div>
    </div>
  );
};

export default AnalysisDetailPage;

